import { EbiCondition, IEbiField, IEbiFieldOption } from "modules/ebiForms/typings";

// Bloki wspólne pakietu H (PCPR i PUP): dane osoby, której dotyczy wniosek składany przez rodzica, opiekuna albo
// pełnomocnika (H1, H4), oraz część finansowa wniosków do PUP — specyfikacja wydatków i zabezpieczenie umowy (H6, H7).

/**
 * Formy zabezpieczenia umowy o dofinansowanie podjęcia działalności i o refundację kosztów wyposażenia stanowiska pracy —
 * § 10 ust. 1 rozporządzenia MRPiPS z dnia 21 listopada 2025 r. (Dz.U. poz. 1645).
 */
export const SECURITY_OPTIONS: IEbiFieldOption[] = [
  { value: "poreczenie", label: "poręczenie" },
  { value: "wekselInBlanco", label: "weksel in blanco" },
  { value: "wekselAval", label: "weksel z poręczeniem wekslowym (aval)" },
  { value: "gwarancja", label: "gwarancja bankowa" },
  { value: "zastaw", label: "zastaw rejestrowy na prawach lub rzeczach" },
  { value: "blokada", label: "blokada środków zgromadzonych na rachunku płatniczym" },
  { value: "aktNotarialny", label: "akt notarialny o poddaniu się egzekucji przez dłużnika" },
];

export const securityField = (visibleWhen?: EbiCondition): IEbiField => ({
  code: "zabezpieczenie",
  type: "checkboxes",
  label: "Proponowana forma zabezpieczenia umowy",
  hint: "Przy wekslu in blanco albo akcie notarialnym o poddaniu się egzekucji trzeba ustanowić dodatkowe zabezpieczenie w innej formie (§ 10 ust. 2 rozporządzenia).",
  required: true,
  visibleWhen,
  options: SECURITY_OPTIONS,
});

/** Poręczenie (zwykłe albo wekslowe) — poręczyciel będący osobą fizyczną składa oświadczenie o dochodach (§ 10 ust. 4). */
export const SURETY: EbiCondition = {
  any: [
    { field: "zabezpieczenie", includes: "poreczenie" },
    { field: "zabezpieczenie", includes: "wekselAval" },
  ],
};

/** Zabezpieczenie, którego dokument ustanawia się przed zawarciem umowy (gwarancja, blokada, akt notarialny). */
export const SECURITY_DOCUMENT: EbiCondition = {
  any: [
    { field: "zabezpieczenie", includes: "gwarancja" },
    { field: "zabezpieczenie", includes: "blokada" },
    { field: "zabezpieczenie", includes: "aktNotarialny" },
  ],
};

/**
 * Szczegółowa specyfikacja wydatków z cenami netto, VAT i brutto (§ 2 ust. 3 pkt 9 i § 6 ust. 3 pkt 6–7 rozporządzenia
 * z 21.11.2025 r.) — lista pozycji zamiast tabeli z wzorów PUP.
 */
export const expensesField = (options: {
  code: string;
  label: string;
  hint?: string;
  visibleWhen?: EbiCondition;
  /** zakup nowy / używany (wniosek o dofinansowanie podjęcia działalności) */
  withCondition?: boolean;
  /** stanowisko pracy i źródło finansowania (wniosek o refundację) */
  withPosition?: boolean;
}): IEbiField => ({
  code: options.code,
  type: "repeat",
  label: options.label,
  hint: options.hint,
  itemLabel: "Wydatek",
  required: true,
  visibleWhen: options.visibleWhen,
  min: 1,
  max: 50,
  fields: [
    { code: "przedmiot", type: "text", label: "Przedmiot zakupu (rodzaj, producent, model, parametry techniczne)", required: true, maxLength: 300 },
    ...(options.withPosition
      ? [{ code: "stanowisko", type: "text", label: "Stanowisko pracy", required: true, width: "half", maxLength: 200 } satisfies IEbiField]
      : []),
    ...(options.withCondition
      ? [
          {
            code: "stan",
            type: "select",
            label: "Zakup",
            required: true,
            width: "half",
            options: [
              { value: "nowy", label: "nowy" },
              { value: "uzywany", label: "używany (podaj rok produkcji w opisie)" },
            ],
          } satisfies IEbiField,
        ]
      : []),
    ...(options.withPosition
      ? [
          {
            code: "zrodlo",
            type: "select",
            label: "Źródło finansowania",
            required: true,
            width: "half",
            options: [
              { value: "refundacja", label: "refundacja z Funduszu Pracy" },
              { value: "wlasne", label: "środki własne" },
              { value: "inne", label: "inne źródła" },
            ],
          } satisfies IEbiField,
        ]
      : []),
    { code: "netto", type: "number", label: "Cena netto [zł]", required: true, width: "third", decimals: 2, min: 0 },
    { code: "vat", type: "number", label: "Podatek VAT [zł]", required: true, width: "third", decimals: 2, min: 0 },
    { code: "brutto", type: "number", label: "Cena brutto [zł]", required: true, width: "third", decimals: 2, min: 0 },
  ],
});

/**
 * Osoba, której dotyczy wniosek, gdy składa go ktoś inny (rodzic, opiekun, kurator, pełnomocnik): imię i nazwisko, PESEL
 * i adres — opcjonalnie z przełącznikiem „ten sam adres co wnioskodawca”.
 */
export const otherPersonFields = (options: {
  /** przedrostek kodów pól, np. „osoba” → osobaImieNazwisko, osobaPesel… */
  prefix: string;
  /** dopełniacz w etykietach, np. „osoby niepełnosprawnej” */
  who: string;
  visibleWhen: EbiCondition;
  addressLabel: string;
  /** etykieta przełącznika „mieszka pod adresem wnioskodawcy” — gdy podana, pola adresu pokazują się po jego odznaczeniu */
  sameAddressLabel?: string;
}): IEbiField[] => {
  const { prefix, who, visibleWhen } = options;
  const sameAddress = `${prefix}AdresWnioskodawcy`;
  const addressVisible: EbiCondition = options.sameAddressLabel
    ? { all: [visibleWhen, { not: { field: sameAddress, equals: true } }] }
    : visibleWhen;

  return [
    { code: `${prefix}ImieNazwisko`, type: "text", label: `Imię i nazwisko ${who}`, required: true, visibleWhen, width: "half", maxLength: 200 },
    { code: `${prefix}Pesel`, type: "pesel", label: `PESEL ${who}`, required: true, visibleWhen, width: "half" },
    ...(options.sameAddressLabel
      ? [{ code: sameAddress, type: "checkbox", label: options.sameAddressLabel, defaultValue: true, visibleWhen } satisfies IEbiField]
      : []),
    {
      code: `${prefix}Adres`,
      type: "text",
      label: `${options.addressLabel} — ulica, numer domu i lokalu`,
      required: true,
      visibleWhen: addressVisible,
      maxLength: 200,
    },
    { code: `${prefix}KodPocztowy`, type: "postalCode", label: "Kod pocztowy", required: true, visibleWhen: addressVisible, width: "third", placeholder: "00-000" },
    { code: `${prefix}Miejscowosc`, type: "text", label: "Miejscowość", required: true, visibleWhen: addressVisible, width: "twoThirds", maxLength: 100 },
  ];
};
